import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { unwrapList } from '../lib/unwrap'

// Wave 1 — Workforce hooks for shifts, checklists, incidents, status beacons,
// and custom map zones. Backed by services/device/internal/handler/workforce_handler.go.

// ── Types ─────────────────────────────────────────────────────────

export interface CrewShift {
  id: string
  driver_user_id: string
  device_id: string | null
  started_at: string
  ended_at: string | null
  planned_end_at: string | null
  status: 'active' | 'break' | 'ended'
  odometer_start: number | null
  odometer_end: number | null
  notes: string
}

export interface ShiftPlanEntry {
  id: string
  driver_user_id: string
  device_id: string | null
  starts_at: string
  ends_at: string
  notes: string
  created_by: string | null
  created_at: string
}

export interface ChecklistTemplate {
  id: string
  name: string
  kind: 'pre_trip' | 'post_trip' | 'incident' | 'custom'
  items: ChecklistTemplateItem[]
  is_active: boolean
}

export interface ChecklistTemplateItem {
  key: string
  label: string
  type: 'bool' | 'text' | 'photo'
  required: boolean
}

export interface ChecklistResponse {
  id: string
  template_id: string | null
  template_kind: string
  driver_user_id: string
  device_id: string | null
  shift_id: string | null
  job_id: string | null
  answers: Record<string, unknown>
  pass: boolean
  submitted_at: string
}

export interface DriverIncident {
  id: string
  driver_user_id: string
  device_id: string | null
  shift_id: string | null
  job_id: string | null
  severity: 'info' | 'minor' | 'major' | 'critical'
  category: string
  description: string
  photo_urls: string[]
  voice_url: string
  latitude: number | null
  longitude: number | null
  status: 'open' | 'acknowledged' | 'resolved'
  occurred_at: string
  resolved_at: string | null
  resolved_by: string | null
  resolution_note: string
}

export interface DriverStatusBeacon {
  driver_user_id: string
  device_id: string | null
  job_id: string | null
  beacon: 'en_route' | 'arrived' | 'loading' | 'unloading' | 'delayed' | 'break' | 'offline'
  note: string
  latitude: number | null
  longitude: number | null
  occurred_at: string
}

export type ZoneCategory = 'depot' | 'customer' | 'forbidden' | 'low_speed' | 'other'

export interface CustomZone {
  id: string
  name: string
  category: ZoneCategory
  color: string
  geometry: unknown
  shape: 'polygon' | 'circle'
  speed_limit_kph: number | null
  metadata: unknown
  created_at: string
  updated_at: string
}

// ── Shifts ────────────────────────────────────────────────────────

export function useShifts(driverUserId?: string) {
  return useQuery({
    queryKey: ['shifts', driverUserId ?? null],
    queryFn: () =>
      api
        .get('/shifts', { params: driverUserId ? { driver_user_id: driverUserId } : undefined })
        .then((r) => unwrapList<CrewShift>(r.data)),
  })
}

export function useShiftPlan(from?: string, to?: string) {
  return useQuery({
    queryKey: ['shift-plan', from ?? null, to ?? null],
    queryFn: () =>
      api
        .get('/shift-plan', { params: { from, to } })
        .then((r) => unwrapList<ShiftPlanEntry>(r.data)),
  })
}

export function useCreateShiftPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<ShiftPlanEntry>) =>
      api.post('/shift-plan', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift-plan'] }),
  })
}

export function useDeleteShiftPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/shift-plan/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift-plan'] }),
  })
}

// ── Status beacons ────────────────────────────────────────────────

export function useLatestStatusBeacons() {
  return useQuery({
    queryKey: ['status-beacons-latest'],
    queryFn: () =>
      api
        .get('/status-beacons/latest')
        .then((r) => unwrapList<DriverStatusBeacon>(r.data)),
    refetchInterval: 30_000,
  })
}

// ── Checklists ────────────────────────────────────────────────────

export function useChecklistTemplates(kind?: string) {
  return useQuery({
    queryKey: ['checklist-templates', kind ?? null],
    queryFn: () =>
      api
        .get('/checklist-templates', { params: kind ? { kind } : undefined })
        .then((r) => unwrapList<ChecklistTemplate>(r.data)),
  })
}

export function useCreateChecklistTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<ChecklistTemplate>) =>
      api.post('/checklist-templates', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checklist-templates'] }),
  })
}

export function useDeleteChecklistTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/checklist-templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checklist-templates'] }),
  })
}

export function useChecklistResponses(driverUserId?: string) {
  return useQuery({
    queryKey: ['checklist-responses', driverUserId ?? null],
    queryFn: () =>
      api
        .get('/checklist-responses', {
          params: driverUserId ? { driver_user_id: driverUserId } : undefined,
        })
        .then((r) => unwrapList<ChecklistResponse>(r.data)),
  })
}

// ── Incidents ─────────────────────────────────────────────────────

export function useIncidents(driverUserId?: string) {
  return useQuery({
    queryKey: ['incidents', driverUserId ?? null],
    queryFn: () =>
      api
        .get('/incidents', {
          params: driverUserId ? { driver_user_id: driverUserId } : undefined,
        })
        .then((r) => unwrapList<DriverIncident>(r.data)),
  })
}

export function useResolveIncident() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      api.post(`/incidents/${id}/resolve`, { note }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['incidents'] }),
  })
}

// ── Custom zones (Live Map 2.0) ───────────────────────────────────

export function useZones(category?: ZoneCategory) {
  return useQuery({
    queryKey: ['zones', category ?? null],
    queryFn: () =>
      api
        .get('/zones', { params: category ? { category } : undefined })
        .then((r) => unwrapList<CustomZone>(r.data)),
  })
}

export function useCreateZone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<CustomZone>) => api.post('/zones', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zones'] }),
  })
}

export function useUpdateZone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: Partial<CustomZone> & { id: string }) =>
      api.put(`/zones/${id}`, patch).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zones'] }),
  })
}

export function useDeleteZone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/zones/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zones'] }),
  })
}
